import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import HelpCenterHeader from './HelpCenterHeader';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { ChevronDownIcon, ArrowRightIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { helpArticleService } from '../services/helpArticleService';
import { helpAIService } from '../services/helpAIService';
import { HelpArticle, HELP_CATEGORIES } from '../types/helpArticle';
import { usePageTitle } from '../hooks/usePageTitle';

const HelpCenter: React.FC = () => {
  const { currentUser } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showWorkspaceDropdown, setShowWorkspaceDropdown] = useState(false);
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<HelpArticle[]>([]);
  const [articlesByCategory, setArticlesByCategory] = useState<Record<string, HelpArticle[]>>({});
  const [loading, setLoading] = useState(true);
  const [aiAnswer, setAiAnswer] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiAnswer, setShowAiAnswer] = useState(false);
  const [relatedArticles, setRelatedArticles] = useState<HelpArticle[]>([]);

  usePageTitle('Help Center');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const [allArticles, featured] = await Promise.all([
        helpArticleService.getAllArticles(),
        helpArticleService.getFeaturedArticles(),
      ]);

      setArticles(allArticles);
      setFeaturedArticles(featured);

      // Group articles by category
      const grouped: Record<string, HelpArticle[]> = {};
      HELP_CATEGORIES.forEach((cat) => {
        grouped[cat.id] = allArticles.filter((a) => a.category === cat.id).slice(0, 3);
      });
      setArticlesByCategory(grouped);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get user's first name
  const firstName = useMemo(() => {
    if (!currentUser) {
      return 'User';
    }
    
    if (currentUser.displayName) {
      const trimmedName = currentUser.displayName.trim();
      if (trimmedName.length > 0) {
        const nameParts = trimmedName.split(/\s+/);
        if (nameParts.length > 0 && nameParts[0] && nameParts[0].length > 0) {
          const first = nameParts[0];
          return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
        }
      }
    }
    
    if (currentUser.email) {
      const emailStr = currentUser.email.trim();
      if (emailStr.length > 0 && emailStr.includes('@')) {
        const emailPart = emailStr.split('@')[0].trim();
        if (emailPart && emailPart.length > 0) {
          const parts = emailPart.split(/[._-]/);
          let first = parts[0] ? parts[0].trim() : '';
          
          if (!first || first.length === 0) {
            first = emailPart;
          }
          
          if (first && first.length > 0) {
            return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
          }
        }
      }
    }
    
    return 'User';
  }, [currentUser]);

  const suggestedQuestions = useMemo(() => {
    // Use featured articles as suggested questions, fallback to default
    if (featuredArticles.length > 0) {
      return featuredArticles.slice(0, 5).map((article) => article.title);
    }
    return [
      "How do I create my first email campaign?",
      "How do I import contacts to Scubamail?",
      "How do I set up email automation?",
      "How do I connect my Shopify store?",
      "How do I verify my email address for sending?",
    ];
  }, [featuredArticles]);

  const handleGetAnswer = async () => {
    if (!searchQuery.trim()) return;
    
    // Show AI answer
    setShowAiAnswer(true);
    setAiLoading(true);
    setAiAnswer(null);
    setRelatedArticles([]);

    try {
      const workspaceContext = currentWorkspace?.name 
        ? `User is working on workspace: ${currentWorkspace.name}. `
        : '';
      
      const response = await helpAIService.answerQuestion(searchQuery, workspaceContext);
      setAiAnswer(response.answer);
      setRelatedArticles(response.relatedArticles);
    } catch (error) {
      console.error('Error getting AI answer:', error);
      setAiAnswer('I encountered an error. Let me search our help articles for you...');
      // Fallback to search
      try {
        const results = await helpArticleService.searchArticles(searchQuery);
        setRelatedArticles(results.slice(0, 5));
      } catch (searchError) {
        console.error('Error searching articles:', searchError);
      }
    } finally {
      setAiLoading(false);
    }
  };

  const handleQuestionClick = async (question: string) => {
    setSearchQuery(question);
    // Use AI to answer the question
    setShowAiAnswer(true);
    setAiLoading(true);
    setAiAnswer(null);
    setRelatedArticles([]);

    try {
      const workspaceContext = currentWorkspace?.name 
        ? `User is working on workspace: ${currentWorkspace.name}. `
        : '';
      
      const response = await helpAIService.answerQuestion(question, workspaceContext);
      setAiAnswer(response.answer);
      setRelatedArticles(response.relatedArticles);
    } catch (error) {
      console.error('Error getting AI answer:', error);
      // Fallback to search
      try {
        const results = await helpArticleService.searchArticles(question);
        if (results.length > 0) {
          const article = results[0];
          if (article.url) {
            window.open(article.url, '_blank');
          } else {
            navigate(`/help/article/${article.slug}`);
          }
        } else {
          navigate(`/help/search?q=${encodeURIComponent(question)}`);
        }
      } catch (searchError) {
        console.error('Error searching:', searchError);
        navigate(`/help/search?q=${encodeURIComponent(question)}`);
      }
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <HelpCenterHeader />

      {/* Hero Section */}
      <main className="w-full bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="container mx-auto px-4 py-8 sm:py-12 lg:py-16">
          <div className="w-full">
            <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            {/* Left Section - Content (2/3) */}
            <div className="lg:col-span-2">
              {/* Scubamail Branded Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1">
                      Scubamail Help Center
                    </h1>
                    <p className="text-gray-600 text-sm sm:text-base">
                      Powered by AI • Get instant answers to your questions
                    </p>
                  </div>
                </div>
                {currentUser && (
                  <p className="text-gray-700 text-base">
                    Welcome back, <span className="font-semibold text-blue-600">{firstName}</span>!
                    {currentWorkspace && (
                      <span className="text-gray-600"> • Working on <span className="font-medium">{currentWorkspace.name}</span></span>
                    )}
                  </p>
                )}
              </div>

              {/* AI-Powered Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                    <SparklesIcon className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !aiLoading && handleGetAnswer()}
                      placeholder="Ask me anything about Scubamail... (e.g., 'How do I create an email campaign?')"
                      className="flex-1 pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-500 shadow-sm"
                      disabled={aiLoading}
                    />
                    <button
                      onClick={handleGetAnswer}
                      disabled={aiLoading || !searchQuery.trim()}
                      className="px-6 py-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-200 whitespace-nowrap shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {aiLoading ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Thinking...</span>
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="w-5 h-5" />
                          <span>Ask AI</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                {!helpAIService.isConfigured() && (
                  <p className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    AI features require API configuration. Showing search results instead.
                  </p>
                )}
              </div>

              {/* AI Answer Display */}
              {showAiAnswer && (
                <div className="mb-6 bg-white rounded-xl border-2 border-blue-200 shadow-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-teal-500 rounded-lg flex items-center justify-center">
                        <SparklesIcon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">AI Answer</h3>
                    </div>
                    <button
                      onClick={() => {
                        setShowAiAnswer(false);
                        setAiAnswer(null);
                        setRelatedArticles([]);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {aiLoading ? (
                    <div className="flex items-center gap-3 text-gray-600">
                      <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Analyzing your question and searching our knowledge base...</span>
                    </div>
                  ) : aiAnswer ? (
                    <>
                      <div className="prose prose-sm max-w-none mb-4">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{aiAnswer}</p>
                      </div>
                      
                      {relatedArticles.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">Related Articles:</h4>
                          <div className="space-y-2">
                            {relatedArticles.map((article) => (
                              article.url ? (
                                <a
                                  key={article.id}
                                  href={article.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block p-3 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors group"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h5 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 mb-1">
                                        {article.title}
                                      </h5>
                                      <p className="text-xs text-gray-600 line-clamp-2">{article.content.substring(0, 100)}...</p>
                                    </div>
                                    <ArrowRightIcon className="w-4 h-4 text-gray-400 group-hover:text-blue-600 ml-2 flex-shrink-0" />
                                  </div>
                                </a>
                              ) : (
                                <Link
                                  key={article.id}
                                  to={`/help/article/${article.slug}`}
                                  className="block p-3 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors group"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h5 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 mb-1">
                                        {article.title}
                                      </h5>
                                      <p className="text-xs text-gray-600 line-clamp-2">{article.content.substring(0, 100)}...</p>
                                    </div>
                                    <ArrowRightIcon className="w-4 h-4 text-gray-400 group-hover:text-blue-600 ml-2 flex-shrink-0" />
                                  </div>
                                </Link>
                              )
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => navigate(`/help/search?q=${encodeURIComponent(searchQuery)}`)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          View all search results →
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              )}

              {/* Suggested Questions */}
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3 font-medium">Popular questions:</p>
                <div className="flex flex-wrap gap-3">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuestionClick(question)}
                      className="px-4 py-2.5 bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-gray-700 hover:text-blue-700 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Section - Illustration (1/3) */}
            <div className="lg:col-span-1 hidden lg:block">
              <div className="relative h-full min-h-[400px] bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 flex items-center justify-center">
                {/* 3D Abstract Illustration Placeholder */}
                <div className="relative w-full h-full">
                  {/* Blue rounded rectangle with icon */}
                  <div className="absolute top-10 left-10 w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center shadow-lg transform rotate-3">
                    <div className="w-8 h-8 border-2 border-white rounded"></div>
                  </div>
                  
                  {/* White cylinder with pin and cursor */}
                  <div className="absolute top-32 right-16 transform -rotate-6">
                    <div className="w-12 h-16 bg-white rounded-full shadow-lg flex items-center justify-center relative">
                      <div className="w-2 h-2 bg-black rounded-full"></div>
                    </div>
                    {/* Mouse cursor pointing at pin */}
                    <div className="absolute -top-2 -left-4 w-6 h-6 bg-white rounded-sm shadow-md transform rotate-45 flex items-center justify-center">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    </div>
                  </div>
                  
                  {/* Orange accordion */}
                  <div className="absolute bottom-24 left-16 w-20 h-12 bg-orange-400 rounded-lg shadow-lg transform rotate-12 flex items-center justify-center">
                    <div className="w-full h-1 bg-orange-300 rounded"></div>
                  </div>
                  
                  {/* Yellow square with A */}
                  <div className="absolute bottom-32 right-10 w-14 h-14 bg-yellow-400 rounded-lg shadow-lg flex items-center justify-center transform -rotate-6">
                    <span className="text-white font-bold text-lg">A</span>
                  </div>
                  
                  {/* Light brown square with dots */}
                  <div className="absolute top-48 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-amber-200 rounded-lg shadow-lg transform rotate-6" style={{
                    backgroundImage: 'radial-gradient(circle, #92400e 1px, transparent 1px)',
                    backgroundSize: '8px 8px'
                  }}></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Explore More - Bottom Center */}
          <div className="flex justify-center items-center mt-6 lg:mt-8">
            <div className="flex items-center gap-2 text-blue-600 hover:text-blue-700 cursor-pointer group">
              <span className="font-medium">Explore More</span>
              <ChevronDownIcon className="w-4 h-4 group-hover:translate-y-1 transition-transform duration-200" />
            </div>
          </div>
        </div>
        </div>
      </main>

      {/* Topics Section */}
      <section className="w-full bg-white py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Find answers by topic
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Browse Knowledge Base articles that have the information you need.
              </p>
            </div>

            {/* Topic Cards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Getting Started Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200">
                {/* Illustration Section */}
                <div className="bg-gray-100 h-48 flex items-center justify-center p-6">
                  <div className="relative w-full h-full">
                    {/* 3D Illustration Elements */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                      {/* Stacked ovals */}
                      <div className="relative">
                        <div className="w-20 h-8 bg-gray-700 rounded-full"></div>
                        <div className="w-16 h-6 bg-orange-400 rounded-full absolute -top-3 left-2"></div>
                        <div className="w-12 h-5 bg-amber-200 rounded-full absolute -top-5 left-4"></div>
                        <div className="w-10 h-4 bg-white rounded-full absolute -top-6 left-5 border-2 border-gray-300 flex items-center justify-center">
                          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                      </div>
                      {/* Blue shape with dots */}
                      <div className="absolute -right-8 top-4 w-16 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <div className="w-12 h-8 bg-blue-400 rounded flex items-center justify-center" style={{
                          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                          backgroundSize: '4px 4px'
                        }}></div>
                      </div>
                      {/* Brown block */}
                      <div className="absolute -left-6 top-8 w-6 h-8 bg-amber-600 rounded-t-lg"></div>
                    </div>
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Getting Started</h3>
                  <ul className="space-y-2 mb-6">
                    {loading ? (
                      <li className="text-gray-500 text-sm">Loading articles...</li>
                    ) : articlesByCategory['getting-started']?.length > 0 ? (
                      articlesByCategory['getting-started'].map((article) => (
                        <li key={article.id} className="flex items-center text-gray-600">
                          <ArrowRightIcon className="w-4 h-4 mr-2 text-blue-500" />
                          {article.url ? (
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </a>
                          ) : (
                            <Link
                              to={`/help/article/${article.slug}`}
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </Link>
                          )}
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500 text-sm">No articles yet</li>
                    )}
                  </ul>
                  <Link
                    to="/help/category/getting-started"
                    className="w-full py-2 px-4 border-2 border-blue-600 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    Show All
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Creating Your Site Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200">
                {/* Illustration Section */}
                <div className="bg-gray-100 h-48 flex items-center justify-center p-6">
                  <div className="relative w-full h-full">
                    {/* 3D Illustration Elements */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                      {/* Stacked shapes */}
                      <div className="relative">
                        <div className="w-24 h-6 bg-gray-700 rounded-lg" style={{
                          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)'
                        }}></div>
                        <div className="w-20 h-5 bg-amber-200 rounded-lg absolute -top-2 left-2 border-l-4 border-teal-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </div>
                        <div className="w-18 h-5 bg-amber-200 rounded-lg absolute -top-4 -left-4 border-l-4 border-amber-800 flex items-center justify-center gap-1">
                          <div className="w-2 h-2 bg-orange-400 rounded"></div>
                          <div className="w-2 h-2 bg-purple-400 rounded"></div>
                          <div className="w-2 h-2 bg-yellow-400 rounded"></div>
                          <svg className="w-3 h-3 text-gray-600 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Email Campaigns</h3>
                  <ul className="space-y-2 mb-6">
                    {loading ? (
                      <li className="text-gray-500 text-sm">Loading articles...</li>
                    ) : articlesByCategory['email-campaigns']?.length > 0 ? (
                      articlesByCategory['email-campaigns'].map((article) => (
                        <li key={article.id} className="flex items-center text-gray-600">
                          <ArrowRightIcon className="w-4 h-4 mr-2 text-blue-500" />
                          {article.url ? (
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </a>
                          ) : (
                            <Link
                              to={`/help/article/${article.slug}`}
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </Link>
                          )}
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500 text-sm">No articles yet</li>
                    )}
                  </ul>
                  <Link
                    to="/help/category/email-campaigns"
                    className="w-full py-2 px-4 border-2 border-blue-600 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    Show All
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Account & Billing Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200">
                {/* Illustration Section */}
                <div className="bg-gray-100 h-48 flex items-center justify-center p-6">
                  <div className="relative w-full h-full">
                    {/* 3D Illustration Elements */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                      {/* Stacked shapes */}
                      <div className="relative">
                        <div className="w-24 h-6 bg-gray-700 rounded-lg" style={{
                          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)'
                        }}></div>
                        <div className="w-20 h-5 bg-orange-400 rounded-lg absolute -top-2 left-2 flex items-center justify-center">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                            <span className="text-orange-600 font-bold text-sm">$</span>
                          </div>
                        </div>
                        <div className="w-18 h-5 bg-blue-500 rounded-lg absolute -top-4 -left-4 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Automations & Workflows</h3>
                  <ul className="space-y-2 mb-6">
                    {loading ? (
                      <li className="text-gray-500 text-sm">Loading articles...</li>
                    ) : articlesByCategory['automations']?.length > 0 ? (
                      articlesByCategory['automations'].map((article) => (
                        <li key={article.id} className="flex items-center text-gray-600">
                          <ArrowRightIcon className="w-4 h-4 mr-2 text-blue-500" />
                          {article.url ? (
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </a>
                          ) : (
                            <Link
                              to={`/help/article/${article.slug}`}
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </Link>
                          )}
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500 text-sm">No articles yet</li>
                    )}
                  </ul>
                  <Link
                    to="/help/category/automations"
                    className="w-full py-2 px-4 border-2 border-blue-600 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    Show All
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Contacts & Segments Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200">
                {/* Illustration Section */}
                <div className="bg-gray-100 h-48 flex items-center justify-center p-6">
                  <div className="relative w-full h-full">
                    {/* 3D Illustration Elements */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                      {/* Stacked shapes */}
                      <div className="relative">
                        <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-teal-400 rounded-lg transform rotate-12"></div>
                        <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-purple-400 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Contacts & Segments</h3>
                  <ul className="space-y-2 mb-6">
                    {loading ? (
                      <li className="text-gray-500 text-sm">Loading articles...</li>
                    ) : articlesByCategory['contacts']?.length > 0 ? (
                      articlesByCategory['contacts'].map((article) => (
                        <li key={article.id} className="flex items-center text-gray-600">
                          <ArrowRightIcon className="w-4 h-4 mr-2 text-blue-500" />
                          {article.url ? (
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </a>
                          ) : (
                            <Link
                              to={`/help/article/${article.slug}`}
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </Link>
                          )}
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500 text-sm">No articles yet</li>
                    )}
                  </ul>
                  <Link
                    to="/help/category/integrations"
                    className="w-full py-2 px-4 border-2 border-blue-600 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    Show All
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Account & Billing Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200">
                {/* Illustration Section */}
                <div className="bg-gray-100 h-48 flex items-center justify-center p-6">
                  <div className="relative w-full h-full">
                    {/* 3D Illustration Elements */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                      {/* Stacked shapes */}
                      <div className="relative">
                        <div className="w-24 h-6 bg-gray-700 rounded-lg" style={{
                          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)'
                        }}></div>
                        <div className="w-20 h-5 bg-orange-400 rounded-lg absolute -top-2 left-2 flex items-center justify-center">
                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                            <span className="text-orange-600 font-bold text-sm">$</span>
                          </div>
                        </div>
                        <div className="w-18 h-5 bg-blue-500 rounded-lg absolute -top-4 -left-4 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Account & Billing</h3>
                  <ul className="space-y-2 mb-6">
                    {loading ? (
                      <li className="text-gray-500 text-sm">Loading articles...</li>
                    ) : articlesByCategory['account-billing']?.length > 0 ? (
                      articlesByCategory['account-billing'].map((article) => (
                        <li key={article.id} className="flex items-center text-gray-600">
                          <ArrowRightIcon className="w-4 h-4 mr-2 text-blue-500" />
                          {article.url ? (
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </a>
                          ) : (
                            <Link
                              to={`/help/article/${article.slug}`}
                              className="cursor-pointer hover:text-blue-600"
                            >
                              {article.title}
                            </Link>
                          )}
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500 text-sm">No articles yet</li>
                    )}
                  </ul>
                  <Link
                    to="/help/category/account-billing"
                    className="w-full py-2 px-4 border-2 border-blue-600 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    Show All
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;

